import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  TrendingUp, 
  FileCheck, 
  Sparkles,
  ChevronRight,
  CheckCircle2,
  Info
} from "lucide-react";
import { Button } from "./ui/button";

const boostItems = [
  {
    id: "invitation",
    title: "Carta de Invitación Notariada",
    description: "Carta certificada por un notario público de EE.UU.",
    bonus: 10,
    icon: FileCheck,
    color: "gold",
    tips: [
      "Debe ser notariada por un notario americano",
      "Incluir copia del ID y prueba de estatus legal del anfitrión",
      "Describe claramente el propósito y duración de la visita",
      "Más efectiva que una carta de invitación simple"
    ],
    fieldName: "has_notarized_invitation"
  }
];

const ApprovalBoostCard = ({ 
  hasNotarizedInvitation = false,
  onUpdate,
  compact = false 
}) => {
  const [expanded, setExpanded] = useState(!compact);

  const totalPotentialBoost = boostItems.reduce((acc, item) => {
    if (item.id === "invitation" && hasNotarizedInvitation) return acc;
    return acc + item.bonus;
  }, 0);

  const earnedBoost = boostItems.reduce((acc, item) => {
    if (item.id === "invitation" && hasNotarizedInvitation) return acc + item.bonus;
    return acc;
  }, 0);

  const getItemStatus = (item) => {
    if (item.id === "invitation") return hasNotarizedInvitation;
    return false;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-elevated overflow-hidden"
      data-testid="approval-boost-card"
    >
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-gold-50 via-amber-50 to-yellow-50 border-b border-gold-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-gold-400 to-amber-500 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-navy-900">
                Aumenta tu Probabilidad
              </h3>
              <p className="text-xs text-navy-600">
                Documentos opcionales que mejoran tu caso
              </p>
            </div>
          </div>
          <div className="text-right">
            {totalPotentialBoost > 0 ? (
              <div className="px-3 py-1 bg-gold-100 rounded-full border border-gold-300" data-testid="boost-available-badge">
                <span className="text-sm font-bold text-gold-700">
                  +{totalPotentialBoost}% disponible
                </span>
              </div>
            ) : (
              <div className="px-3 py-1 bg-emerald-100 rounded-full border border-emerald-300" data-testid="boost-earned-badge">
                <span className="text-sm font-bold text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  +{earnedBoost}% obtenido
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Info banner */}
        <div className="mt-3 p-2 bg-white/60 rounded-lg flex items-start gap-2">
          <Info className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-navy-700">
            <strong>Más documentos = Mayor probabilidad de éxito.</strong> Estos documentos no son obligatorios, 
            pero demuestran al cónsul que tienes un plan serio y aumentan significativamente tus probabilidades.
          </p>
        </div>
      </div>

      {/* Items */}
      <div className="p-4 space-y-3">
        {boostItems.map((item, idx) => {
          const isCompleted = getItemStatus(item);
          const IconComponent = item.icon;
          
          return (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
              data-testid={`boost-item-${item.id}`}
              className={`p-4 rounded-xl border-2 transition-all ${
                isCompleted 
                  ? "bg-emerald-50 border-emerald-300" 
                  : "bg-gradient-to-r from-gold-50 to-amber-50 border-gold-200 hover:border-gold-300"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  isCompleted 
                    ? "bg-emerald-200" 
                    : "bg-gradient-to-br from-gold-300 to-amber-400"
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 className="w-6 h-6 text-emerald-700" data-testid={`boost-item-${item.id}-completed-icon`} />
                  ) : (
                    <IconComponent className="w-6 h-6 text-white" />
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-semibold ${isCompleted ? "text-emerald-800" : "text-navy-900"}`}>
                      {item.title}
                    </h4>
                    <span data-testid={`boost-item-${item.id}-badge`} className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      isCompleted 
                        ? "bg-emerald-200 text-emerald-800" 
                        : "bg-gold-200 text-gold-800"
                    }`}>
                      {isCompleted ? `+${item.bonus}% aplicado` : `+${item.bonus}%`}
                    </span>
                  </div>
                  <p className={`text-sm ${isCompleted ? "text-emerald-700" : "text-navy-600"}`}>
                    {item.description}
                  </p>
                  
                  {/* Tips - show when not completed */}
                  {!isCompleted && (
                    <div className="mt-3 space-y-1">
                      {item.tips.slice(0, 2).map((tip, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-navy-600">
                          <TrendingUp className="w-3 h-3 text-gold-500" />
                          {tip}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action */}
              {!isCompleted && onUpdate && (
                <div className="mt-3 pt-3 border-t border-gold-200 flex items-center justify-between">
                  <span className="text-xs text-navy-500">
                    ¿Ya tienes este documento?
                  </span>
                  <Button
                    size="sm"
                    className="bg-gold-600 hover:bg-gold-700 text-white"
                    onClick={() => onUpdate(item.fieldName, true)}
                    data-testid={`boost-item-${item.id}-mark-btn`}
                  >
                    Marcar como listo
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* CTA to Documents */}
      <div className="p-4 bg-navy-50 border-t border-navy-100">
        <Link to="/step/documents">
          <Button variant="outline" className="w-full" data-testid="view-all-documents-btn">
            Ver todos los documentos requeridos
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

export default ApprovalBoostCard;
